/** @format */

import { useState, useEffect } from "@wordpress/element";
import Swal from "sweetalert2";
import AthleteTable from "./AthleteTable.jsx";
import AthleteModalWorkspace from "./AthleteModalWorkspace.jsx";

const AthleteManager = ({ meetingPostId, ageStandard = "standard_intl", registrationDeadline, onDataMutation }) => {
    const [team, setTeam] = useState([]);
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAthlete, setSelectedAthlete] = useState(null);
    const [processingEventKey, setProcessingEventKey] = useState(null);

    const isDeadlinePassed = () => {
        if(!registrationDeadline) return false;
        return new Date().getTime() >= new Date(registrationDeadline).getTime();
    };

    const loadAthleteWorkspaceData = () => {
        const globalData = window.ammData;
        if(!globalData?.api_url || !meetingPostId) {
            setLoading(false);
            return;
        }

        setLoading(true);

        // Target your specific, high-performance isolated backend routes
        const teamUrl = `${ globalData.api_url }/my-roster?standard=${ encodeURIComponent(ageStandard) }&meetingId=${ encodeURIComponent(meetingPostId) }`;
        const entriesUrl = `${ globalData.api_url }/meetings/${ meetingPostId }/athlete-entries`;

        Promise.all([
            fetch(teamUrl, { headers: { "X-WP-Nonce": globalData.nonce } }).then((res) => res.json()),
            fetch(entriesUrl, { headers: { "X-WP-Nonce": globalData.nonce } }).then((res) => res.json())
        ])
            .then(([teamData, entriesData]) => {
                setTeam(Array.isArray(teamData) ? teamData : []);
                setEntries(Array.isArray(entriesData) ? entriesData : []);
                setLoading(false)
            })
            .catch((err) => {
                console.error("Failed loading athlete registration workspace:", err);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadAthleteWorkspaceData();
    }, [meetingPostId, ageStandard]);

    const handleOpenWorkspace = (athlete) => {
        setProcessingEventKey(null);
        setSelectedAthlete(athlete);

        if(isDeadlinePassed()) {
            Swal.fire({
                title: "Entry Deadline Passed",
                text: "The official entry deadline for this competition has passed. View configuration is in read-only mode.",
                icon: "warning",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Review Workspace",
                customClass: { container: "amm-swal-container-override" },
            });
        }
    };

    const handleCloseWorkspace = () => {
        setSelectedAthlete(null);
        setProcessingEventKey(null);
        if(typeof onDataMutation === "function") {
            onDataMutation();
        }
    };

    if(loading) {
        return (
            <div className="text-muted py-4 text-center">
                <span className="spinner-border spinner-border-sm me-2"></span>Loading team assets...
            </div>
        );
    }

    if(!team || team.length === 0) {
        return (
            <div className="alert alert-info mb-0">
                You haven't added any athletes to your database yet. Please go to the <strong>My Athletes</strong> dashboard to manage your team setup.
            </div>
        );
    }

    return (
        <div className="athlete-team-workspace">
            <style>{ `
				.amm-swal-container-override, .swal2-container { z-index: 110050 !important; }
				.amm-modal-overlay {
					position: fixed; top: 0; left: 0; width: 100%; height: 100%;
					background: rgba(0,0,0,0.5);
					display: flex; align-items: center; justify-content: center;
				}
				.amm-modal-content { max-height: 90vh; overflow-y: auto; }
			`}</style>

            <h4 className="fw-bold mb-3 p-0">Team Entry Sheet</h4>
            <p className="text-muted small mb-3">Click "Select Events" next to an athlete's name to manage entry profiles inside the selection modal overlay.</p>

            <AthleteTable
                team={ team }
                entries={ entries }
                isDeadlinePassed={ isDeadlinePassed() }
                onSelectAthlete={ handleOpenWorkspace }
            />

            { selectedAthlete && (
                <AthleteModalWorkspace
                    selectedAthlete={ selectedAthlete }
                    entries={ entries }
                    setEntries={ setEntries }
                    meetingPostId={ meetingPostId }
                    ageStandard={ ageStandard }
                    processingEventKey={ processingEventKey }
                    setProcessingEventKey={ setProcessingEventKey }
                    isDeadlinePassed={ isDeadlinePassed() }
                    onClose={ handleCloseWorkspace }
                />
            ) }
        </div>
    );
};

export default AthleteManager;